'use client';

import React from 'react';
import { Wrench } from 'lucide-react';
import type { PostCardProps } from './post-card';
import { stripMarkdown } from '@/lib/community/utils';
import { SmartCoverImage } from './smart-cover-image';
import { PostCardStats } from './post-card-stats';

export const PostCardTool = React.memo(function PostCardTool({ post, onClick, onTagClick, onLike, onFavorite }: PostCardProps) {
	return (
		<div
			className="liquid-glass-card rounded-md overflow-hidden cursor-pointer"
			onClick={(e) => {
				const card = e.currentTarget;
				const cover = card.querySelector('[data-cover]');
				onClick?.(post, card.getBoundingClientRect(), cover?.getBoundingClientRect() ?? undefined);
			}}
		>
			{post.coverImage && (
				<div data-cover>
					<SmartCoverImage alt={post.title} src={post.coverImage} />
				</div>
			)}

			<div className="p-3 flex flex-col gap-2">
				<h3 className="font-medium text-sm leading-snug truncate">{post.title}</h3>

				<p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{stripMarkdown(post.description)}</p>

				{post.toolDetail?.tools && post.toolDetail.tools.length > 0 && (
					<div className="flex items-start gap-1.5 rounded-md border border-amber-500/25 bg-amber-500/10 px-2 py-1.5 w-fit">
						<Wrench className="w-3 h-3 text-amber-700 dark:text-amber-500 shrink-0 mt-0.5" />
						<div className="flex flex-wrap gap-1">
							{post.toolDetail.tools.slice(0, 3).map((tool) => (
								<span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-800/10 text-amber-800 dark:text-amber-400/80" key={tool.id}>
									{tool.label}
								</span>
							))}
							{post.toolDetail.tools.length > 3 && <span className="text-[10px] text-amber-700/60 dark:text-amber-500/50">+{post.toolDetail.tools.length - 3}</span>}
						</div>
					</div>
				)}

				{post.compatibleAgents && post.compatibleAgents.length > 0 && (
					<div className="flex flex-col gap-1">
						<span className="text-[10px] text-muted-foreground font-medium">兼容 Agent</span>
						<div className="flex flex-wrap gap-1">
							{post.compatibleAgents.slice(0, 3).map((agent) => (
								<span
									className="inline-block rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 px-2 py-0.5 text-[10px]"
									key={agent}
								>
									{agent}
								</span>
							))}
						</div>
					</div>
				)}

				{post.tags.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{post.tags.slice(0, 3).map((tag) => (
							<button
								className="inline-block rounded-full bg-secondary/70 text-foreground/70 px-2 py-0.5 text-[10px] hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
								key={tag}
								onClick={(e) => {
									e.stopPropagation();
									onTagClick?.(tag);
								}}
								type="button"
							>
								#{tag}
							</button>
						))}
					</div>
				)}

				<PostCardStats onFavorite={onFavorite} onLike={onLike} post={post} />
			</div>
		</div>
	);
});
